import React, { useEffect, useState } from 'react';
import { Loader2, Lock, RefreshCw, Unlock } from 'lucide-react';
import api from '@/api/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';

const formatDate = (value) => {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleDateString('pt-PT');
  } catch {
    return value;
  }
};

const formatCurrency = (value) => `€${Number(value || 0).toFixed(2)}`;

const getStatusBadge = (status) => {
  const map = {
    open: 'bg-emerald-100 text-emerald-800',
    closed: 'bg-muted text-muted-foreground',
    reopened: 'bg-amber-100 text-amber-800',
  };
  return map[status] || 'bg-muted text-muted-foreground';
};

const getStatusText = (status) => {
  const map = { open: 'Aberta', closed: 'Fechada', reopened: 'Reaberta' };
  return map[status] || status;
};

export default function WeekManagement() {
  const [weeks, setWeeks] = useState([]);
  const [current, setCurrent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [showReopen, setShowReopen] = useState(false);
  const [reopenReason, setReopenReason] = useState('');
  const [selected, setSelected] = useState(null);

  const load = async () => {
    try {
      setLoading(true);
      const [listRes, currentRes] = await Promise.all([
        api.get('/weeks'),
        api.get('/weeks/current').catch(() => ({ data: { data: null } })),
      ]);
      setWeeks(listRes.data?.data || []);
      setCurrent(currentRes.data?.data || null);
    } catch {
      toast.error('Erro ao carregar semanas');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const openWeek = async () => {
    if (!window.confirm('Deseja abrir uma nova semana financeira?')) return;
    setBusy('open');
    try {
      await api.post('/weeks/open');
      toast.success('Semana aberta');
      load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Erro ao abrir semana');
    } finally {
      setBusy(null);
    }
  };

  const closeWeek = async (id) => {
    if (!window.confirm('Deseja fechar esta semana?')) return;
    setBusy(id);
    try {
      await api.post(`/weeks/${id}/close`);
      toast.success('Semana fechada');
      load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Erro ao fechar semana');
    } finally {
      setBusy(null);
    }
  };

  const reopen = async () => {
    if (!reopenReason.trim()) {
      toast.error('Indique o motivo da reabertura');
      return;
    }
    setBusy('reopen');
    try {
      await api.post(`/weeks/${selected.id}/reopen`, { reason: reopenReason });
      toast.success('Semana reaberta');
      setShowReopen(false);
      setReopenReason('');
      setSelected(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Erro ao reabrir semana');
    } finally {
      setBusy(null);
    }
  };

  if (loading) {
    return (
      <div className="p-12 text-center">
        <Loader2 className="mx-auto h-6 w-6 animate-spin text-primary" />
        <p className="mt-3 text-sm text-muted-foreground">Carregando Semanas...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold tracking-tight">Semanas Financeiras</h1>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={load} disabled={loading}>
            <RefreshCw size={16} className="mr-2" /> Atualizar
          </Button>
          <Button onClick={openWeek} disabled={busy === 'open'}>
            {busy === 'open' ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Unlock size={16} className="mr-2" />}
            Abrir Semana
          </Button>
        </div>
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Semana Atual</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {current ? (
              <>
                <div className="flex items-center gap-2">
                  <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${getStatusBadge(current.status)}`}>
                    {getStatusText(current.status)}
                  </span>
                  <span className="text-sm font-medium">{current.week_number}/{current.year}</span>
                </div>
                <div className="space-y-1 text-sm">
                  <p>
                    <span className="text-muted-foreground">Período:</span> {formatDate(current.start_date)} - {formatDate(current.end_date)}
                  </p>
                  <p>
                    <span className="text-muted-foreground">Viagens:</span> {current.total_trips}
                  </p>
                  <p>
                    <span className="text-muted-foreground">Receita:</span> {formatCurrency(current.total_revenue)}
                  </p>
                  <p>
                    <span className="text-muted-foreground">Comissão:</span> {formatCurrency(current.total_commission)}
                  </p>
                </div>
                {current.status === 'open' && (
                  <Button onClick={() => closeWeek(current.id)} disabled={busy === current.id} className="w-full">
                    {busy === current.id ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Lock size={16} className="mr-2" />}
                    Fechar Semana
                  </Button>
                )}
              </>
            ) : (
              <p className="text-sm text-muted-foreground">Nenhuma semana aberta.</p>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Ações Rápidas</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <Button onClick={openWeek} disabled={busy === 'open' || current?.status === 'open'} className="w-full">
              {busy === 'open' ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Unlock size={16} className="mr-2" />}
              Abrir Nova Semana
            </Button>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Histórico</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-left text-muted-foreground">
                  <th className="py-2">Semana</th>
                  <th>Período</th>
                  <th>Status</th>
                  <th className="text-right">Viagens</th>
                  <th className="text-right">Receita</th>
                  <th className="text-right">Ações</th>
                </tr>
              </thead>
              <tbody>
                {weeks.map((w) => (
                  <tr key={w.id} className="border-b">
                    <td className="py-2 font-medium">{w.week_number}/{w.year}</td>
                    <td>
                      {formatDate(w.start_date)} - {formatDate(w.end_date)}
                    </td>
                    <td>
                      <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${getStatusBadge(w.status)}`}>
                        {getStatusText(w.status)}
                      </span>
                    </td>
                    <td className="text-right">{w.total_trips}</td>
                    <td className="text-right">{formatCurrency(w.total_revenue)}</td>
                    <td className="text-right">
                      {w.status === 'closed' && (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => {
                            setSelected(w);
                            setShowReopen(true);
                          }}
                        >
                          Reabrir
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {showReopen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <Card className="w-full max-w-md">
            <CardHeader>
              <CardTitle>Reabrir {selected?.week_number}/{selected?.year}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">Motivo</label>
                <textarea
                  value={reopenReason}
                  onChange={(e) => setReopenReason(e.target.value)}
                  rows={4}
                  className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  placeholder="Descreva o motivo..."
                />
              </div>
              <div className="flex gap-2">
                <Button onClick={reopen} disabled={busy === 'reopen'} className="flex-1">
                  {busy === 'reopen' ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : 'Confirmar'}
                </Button>
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowReopen(false);
                    setReopenReason('');
                    setSelected(null);
                  }}
                  className="flex-1"
                >
                  Cancelar
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
